import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';

/**
 * Closing the account.
 *
 * ## Three things stand between here and gone
 *
 * A press to reveal it, the account's own name typed out, and the password.
 * That is more friction than anything else in the product, deliberately: this
 * is the one action with no undo, and the cost of making it slightly annoying
 * is nothing next to the cost of somebody doing it by accident.
 *
 * Typing the username is the part that does the real work. A password field
 * alone is answered on reflex by anybody who has just typed their password
 * somewhere else; typing your own name is a sentence you have to mean.
 *
 * ## It says what actually happens
 *
 * Not "this cannot be undone" and nothing else. People are owed the specifics
 * before they decide - which of their things disappear, which stay because
 * other people are in them, and what happens to the address they signed up
 * with.
 */
export function DeleteAccount() {
  const user = useSession((state) => state.user);
  const logout = useSession((state) => state.logout);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');

  const remove = useMutation({
    mutationFn: () => api.delete('/me', { body: { password } }),
    onSuccess: () => {
      // The tokens are already revoked server-side; this clears the client so
      // the next render is not a signed-in shell with no account behind it.
      void logout();
      navigate('/');
    },
  });

  const named = user?.username ?? '';
  const ready = confirm.trim() === named && password !== '';

  return (
    <Card className="border-danger/30 p-6">
      <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-faint">
        Delete account
      </h2>

      {!open ? (
        <>
          <p className="mb-4 text-sm text-soft">
            Closes your account and removes your tracks for good.
          </p>

          <Button variant="secondary" onClick={() => setOpen(true)} data-delete-account>
            Delete my account
          </Button>
        </>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();

            if (ready && !remove.isPending) remove.mutate();
          }}
        >
          <div className="text-sm text-soft">
            <p className="mb-2">This cannot be undone. When you confirm:</p>

            <ul className="ml-4 list-disc space-y-1 text-faint">
              <li>your tracks, their audio and their artwork are deleted</li>
              <li>your playlists and albums are deleted</li>
              <li>your profile stops existing and your name is retired</li>
              <li>
                messages you sent stay in other people’s conversations, shown as
                from a deleted account
              </li>
              <li>your email address is freed, so you could sign up again later</li>
            </ul>
          </div>

          <Field
            label={`Type ${named} to confirm`}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="off"
            data-delete-confirm
          />

          <Field
            label="Your password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            data-delete-password
          />

          {remove.isError && (
            <Alert tone="error">
              That did not go through. Check your password and try again.
            </Alert>
          )}

          <div className="flex items-center gap-2">
            <Button
              type="submit"
              variant="danger"
              disabled={!ready}
              loading={remove.isPending}
              data-delete-submit
            >
              Delete my account
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setOpen(false);
                setConfirm('');
                setPassword('');
              }}
            >
              Keep it
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
